import { create } from 'zustand'
import { apiClient } from '../lib/api'
import toast from 'react-hot-toast'

export interface ReportFilters {
  date_range?: 'today' | 'yesterday' | '7d' | '30d' | 'this_month' | 'last_month' | 'custom' | string
  from?: string
  to?: string
  time_from?: string
  time_to?: string
  group_by?: 'hour' | 'day' | 'week' | 'month'
  ticket_type?: string[]
  status?: string[]
  priority?: string[]
  agent_ids?: string[]
  team_ids?: string[]
  source?: string[]
  search?: string
}

export interface TicketReportData {
  totals: {
    totalCreated: number
    totalResolved: number
    activeCount: number
    avgResolutionTime: string
    avgResolutionHours: number
    avgFirstResponseTime: string
    reopenedCount: number
  }
  byStatus: Array<{ status: string; count: number; pct: number }>
  byPriority: Array<{ priority: string; count: number; pct: number; color?: string }>
  byType: Array<{ name: string; code: string; prefix: string; count: number; sla: string; pct: number }>
  byCategory: Array<{ cat: string; count: number; pct: number }>
  bySource: Array<{ source: string; count: number; pct: number }>
  trend: Array<{ date: string; label: string; created: number; resolved: number }>
  backlogAging: Array<{ range: string; count: number; pct: number }>
}

export interface AgentReportItem {
  agentId: number
  name: string
  email: string
  department?: string
  assigned: number
  resolved: number
  active: number
  avgResolutionTime: string
  avgFirstResponse: string
  slaMetPct: string
  csatAvg: number
  workload: string
  trend: '↑' | '→' | '↓'
}

export interface AgentReportData {
  summary: {
    totalAgents: number
    totalAssigned: number
    totalResolved: number
    avgResolutionTime: string
    avgSlaMet: string
    avgCsat: number
  }
  agents: AgentReportItem[]
}

export interface CsatReportData {
  avgScore: number
  totalSurveysSent: number
  totalResponses: number
  responseRate: string
  npsScore: string
  ratingDistribution: Array<{ stars: number; count: number; pct: number; label: string; color: string }>
  trend: Array<{ date: string; label: string; avgRating: number; count: number }>
  byAgent: Array<{ agentId: number; name: string; count: number; avgRating: number }>
  byTeam: Array<{ teamId: number; name: string; count: number; avgRating: number }>
  recentComments: Array<{
    id: number
    ticketId: number
    ticketNo: string
    subject?: string
    rating: number
    comment: string
    agentName: string
    teamName: string
    submittedAt: string
  }>
}

export interface SlaQueueItem {
  key: string
  label: string
  total: number
  met: number
  breached: number
  compliance: number
}

export interface SlaPriorityItem {
  priority: string
  compliance: number
  target: number
  breaches: number
  total: number
}

export interface SlaReportData {
  overallCompliance: string
  totalRequests: number
  totalMet: number
  totalBreached: number
  urgentEscalations: number
  byDepartment: SlaQueueItem[]
  byOriginModule: SlaQueueItem[]
  bySource: SlaQueueItem[]
  byPriority: SlaPriorityItem[]
}

export interface ReportScheduleRecord {
  id: number
  name: string
  report_type: string
  filters: ReportFilters
  channel: string
  webhook_url?: string
  recipients?: string
  cron: string
  last_sent_at?: string
  created_by?: string
  is_active: boolean
  created_at: string
}

/** Format a Date as local 24h `YYYY-MM-DD HH:mm:ss`. */
export const formatLocalDateTime = (d: Date): string => {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
};

/** Default range: past 30 days → today 23:59:59 so full current active dataset and report baselines align with tickets. */
export const getInitialSystemDateRange = () => {
  const now = new Date();
  const past30d = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
  const start = new Date(past30d.getFullYear(), past30d.getMonth(), past30d.getDate(), 0, 0, 0);
  const end = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59);
  return {
    from: formatLocalDateTime(start),
    to: formatLocalDateTime(end),
  };
};

/** Ensure a timestamp has 24h time; defaults to 00:00:00 for start or 23:59:59 for end. Supports both ':' and '.' in time string. */
export const formatDisplayDateTime = (val?: string, isEnd = false): string => {
  if (!val) return '';
  const s = val.trim();
  const m = /^(\d{4}-\d{2}-\d{2})(?:[ T](\d{2})[:.](\d{2})(?:[:.](\d{2}))?)?$/.exec(s);
  if (!m) return s;
  const date = m[1];
  const time = m[2] !== undefined
    ? `${m[2]}:${m[3]}:${m[4] ?? (isEnd ? '59' : '00')}`
    : (isEnd ? '23:59:59' : '00:00:00');
  return `${date} ${time}`;
};

/** Convert a local `YYYY-MM-DD HH:mm:ss` string to a UTC ISO string; pass through anything unparseable. */
const localToIso = (value: string, endOfDay = false): string => {
  const v = value.trim();
  const m = /^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2})[:.](\d{2})(?:[:.](\d{2}))?)?$/.exec(v);
  if (!m) return v;
  const dateOnly = m[4] === undefined;
  const [h, mi, s] = dateOnly && endOfDay ? [23, 59, 59] : [+(m[4] ?? 0), +(m[5] ?? 0), +(m[6] ?? 0)];
  const d = new Date(+m[1], +m[2] - 1, +m[3], h, mi, s);
  return isNaN(d.getTime()) ? v : d.toISOString();
};

export const getDefaultReportFilters = (): ReportFilters => {
  const init = getInitialSystemDateRange();
  return {
    date_range: '30d',
    from: init.from,
    to: init.to,
    time_from: '',
    time_to: '',
    group_by: 'day',
    ticket_type: [],
    status: [],
    priority: [],
    agent_ids: [],
    team_ids: [],
    source: [],
    search: '',
  };
};

const defaultFilters: ReportFilters = getDefaultReportFilters();

interface SupportReportState {
  filters: ReportFilters
  autoRefreshInterval: number // in seconds, 0 = off
  ticketReport: TicketReportData | null
  agentReport: AgentReportData | null
  csatReport: CsatReportData | null
  slaReport: SlaReportData | null
  schedules: ReportScheduleRecord[]
  isLoading: boolean
  isExporting: boolean
  error: string | null

  // Actions
  setFilter: <K extends keyof ReportFilters>(key: K, value: ReportFilters[K]) => void
  setFilters: (newFilters: Partial<ReportFilters>) => void
  resetFilters: () => void
  setAutoRefreshInterval: (interval: number) => void
  fetchActiveTabReport: (activeTab: 'ticket' | 'agent' | 'csat' | 'sla') => Promise<void>
  fetchAllReports: () => Promise<void>
  exportReportFile: (type: 'all' | 'tickets' | 'agents' | 'csat' | 'sla', format: 'csv' | 'xlsx' | 'json') => Promise<void>
  dispatchSummaryCard: (payload: {
    channel: 'gchat_webhook' | 'email' | 'both'
    webhook_url?: string
    recipients?: string
    customTitle?: string
    type?: 'daily' | 'weekly' | 'monthly' | 'custom'
    schedule?: { enabled: boolean; name?: string; cron?: string }
  }) => Promise<boolean>
  fetchSchedules: () => Promise<void>
  deleteSchedule: (id: number) => Promise<void>
  sendScheduleNow: (id: number) => Promise<void>
}

// Helper to convert filters to query parameters
export function serializeFiltersToParams(filters: ReportFilters): Record<string, string> {
  const params: Record<string, string> = {}
  if (filters.date_range) params.date_range = filters.date_range
  if (filters.from) params.from = localToIso(filters.from)
  if (filters.to) params.to = localToIso(filters.to, true)
  if (filters.time_from) params.time_from = filters.time_from
  if (filters.time_to) params.time_to = filters.time_to
  if (filters.group_by) params.group_by = filters.group_by
  if (filters.ticket_type && filters.ticket_type.length > 0) params.ticket_type = filters.ticket_type.join(',')
  if (filters.status && filters.status.length > 0) params.status = filters.status.join(',')
  if (filters.priority && filters.priority.length > 0) params.priority = filters.priority.join(',')
  if (filters.agent_ids && filters.agent_ids.length > 0) params.agent_ids = filters.agent_ids.join(',')
  if (filters.team_ids && filters.team_ids.length > 0) params.team_ids = filters.team_ids.join(',')
  if (filters.source && filters.source.length > 0) params.source = filters.source.join(',')
  if (filters.search) params.search = filters.search
  return params
}

export const useSupportReportStore = create<SupportReportState>((set, get) => ({
  filters: { ...defaultFilters },
  autoRefreshInterval: 0,
  ticketReport: null,
  agentReport: null,
  csatReport: null,
  slaReport: null,
  schedules: [],
  isLoading: false,
  isExporting: false,
  error: null,

  setFilter: (key, value) => {
    set((state) => ({
      filters: { ...state.filters, [key]: value },
    }))
  },

  setFilters: (newFilters) => {
    set((state) => ({
      filters: { ...state.filters, ...newFilters },
    }))
  },

  resetFilters: () => {
    set({ filters: getDefaultReportFilters() })
  },

  setAutoRefreshInterval: (interval) => {
    set({ autoRefreshInterval: interval })
  },

  fetchActiveTabReport: async (activeTab) => {
    set({ isLoading: true, error: null })
    const { filters } = get()
    const params = serializeFiltersToParams(filters)

    try {
      if (activeTab === 'ticket') {
        const res = await apiClient.get('/support/reports/tickets', { params })
        set({ ticketReport: res.data?.data || null, isLoading: false })
      } else if (activeTab === 'agent') {
        const res = await apiClient.get('/support/reports/agents', { params })
        set({ agentReport: res.data?.data || null, isLoading: false })
      } else if (activeTab === 'csat') {
        const res = await apiClient.get('/support/reports/csat', { params })
        set({ csatReport: res.data?.data || null, isLoading: false })
      } else if (activeTab === 'sla') {
        const res = await apiClient.get('/support/reports/sla', { params })
        set({ slaReport: res.data?.data || null, isLoading: false })
      }
    } catch (err: any) {
      const msg = err.response?.data?.message || err.message || 'Failed to fetch report data'
      set({ error: msg, isLoading: false })
      toast.error(msg)
    }
  },

  fetchAllReports: async () => {
    set({ isLoading: true, error: null })
    const { filters } = get()
    const params = serializeFiltersToParams(filters)

    try {
      const [tRes, aRes, cRes, sRes] = await Promise.all([
        apiClient.get('/support/reports/tickets', { params }),
        apiClient.get('/support/reports/agents', { params }),
        apiClient.get('/support/reports/csat', { params }),
        apiClient.get('/support/reports/sla', { params }),
      ])

      set({
        ticketReport: tRes.data?.data || null,
        agentReport: aRes.data?.data || null,
        csatReport: cRes.data?.data || null,
        slaReport: sRes.data?.data || null,
        isLoading: false,
      })
    } catch (err: any) {
      const msg = err.response?.data?.message || err.message || 'Failed to fetch all reports'
      set({ error: msg, isLoading: false })
      toast.error(msg)
    }
  },

  exportReportFile: async (type, format) => {
    set({ isExporting: true })
    const { filters } = get()
    const params = {
      ...serializeFiltersToParams(filters),
      type,
      format,
    }

    try {
      const res = await apiClient.get('/support/reports/export', {
        params,
        responseType: 'blob',
      })

      const blob = new Blob([res.data], {
        type:
          format === 'json'
            ? 'application/json'
            : format === 'xlsx'
            ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
            : 'text/csv;charset=utf-8',
      })

      const extension = format
      const pad = (n: number) => String(n).padStart(2, '0')
      const now = new Date()
      const stamp = `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}`
      const filename = `Support_Report_${type}_${filters.date_range || 'custom'}_${stamp}.${extension}`

      const link = document.createElement('a')
      link.href = URL.createObjectURL(blob)
      link.setAttribute('download', filename)
      document.body.appendChild(link)
      link.click()
      link.remove()
      URL.revokeObjectURL(link.href)

      toast.success(`Exported ${format.toUpperCase()} successfully`)
    } catch (err: any) {
      toast.error(`Export failed: ${err.message || 'Error downloading file'}`)
    } finally {
      set({ isExporting: false })
    }
  },

  dispatchSummaryCard: async (payload) => {
    const { filters } = get()
    try {
      const body = {
        ...payload,
        filters,
      }
      const res = await apiClient.post('/support/reports/summary-card', body)
      if (res.data?.success) {
        toast.success('Summary card successfully dispatched!')
        return true
      } else {
        toast.error('Failed to dispatch summary card')
        return false
      }
    } catch (err: any) {
      toast.error(err.response?.data?.message || err.message || 'Failed to send summary card')
      return false
    }
  },

  fetchSchedules: async () => {
    try {
      const res = await apiClient.get('/support/reports/schedules')
      set({ schedules: res.data?.data || [] })
    } catch (err: any) {
      console.warn('Failed to fetch schedules', err)
    }
  },

  deleteSchedule: async (id) => {
    try {
      await apiClient.delete(`/support/reports/schedules/${id}`)
      set((state) => ({
        schedules: state.schedules.filter((s) => s.id !== id),
      }))
      toast.success('Schedule deleted successfully')
    } catch (err: any) {
      toast.error('Failed to delete schedule')
    }
  },

  sendScheduleNow: async (id) => {
    try {
      await apiClient.post(`/support/reports/schedules/${id}/send-now`)
      toast.success('Schedule triggered successfully!')
    } catch (err: any) {
      toast.error('Failed to run schedule')
    }
  },
}))
